"use client";

import React from "react";
import { motion } from "framer-motion";

export interface MemorylayerHeroBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    serviceScope?: string;
    clientFocus?: string;
    buildWindow?: string;
    stats?: Array<{ metric: string; label: string }>;
}

export default function MemorylayerHeroBlock({
    tag = "Autonomous ML Blueprint",
    title = "The Context Layer for Agentic AI",
    description = "A high-performance conversational AI system and prompt compiler. Instantiate custom behavioral agents, configure persistent Vector Memory pools, and stream localized conversations across 40+ dialects.",
    serviceScope = "LLM Fine-Tuning, UX Architecture, DevOps",
    clientFocus = "MemoryLayer AI Engine",
    buildWindow = "6 Months",
    stats = [
        { metric: "<100ms", label: "First Token Latency" },
        { metric: "40+", label: "Polyglot Languages" },
        { metric: "15k+", label: "Embedding Lookups/s" },
        { metric: "6 Months", label: "Discovery to Production" }
    ]
}: MemorylayerHeroBlockProps) {
    return (
        <div className="w-full bg-[#0A0A0C] overflow-hidden">
            {/* Cyberpunk Hero Section */}
            <section className="relative min-h-[95vh] pt-32 pb-20 px-6 md:px-12 lg:px-24 xl:px-32 flex items-center bg-[radial-gradient(#ffffff04_1px,transparent_1px)] [background-size:24px_24px] w-full" data-header-theme="light">
                {/* Background Ambient Glows */}
                <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[550px] h-[550px] bg-purple-500/10 rounded-full blur-[130px] pointer-events-none" />
                <div className="absolute top-1/3 right-1/4 w-[350px] h-[350px] bg-indigo-500/10 rounded-full blur-[110px] pointer-events-none" />
                
                <div className="container mx-auto z-10 w-full">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                        
                        {/* Left Column: Headlines */}
                        <div className="lg:col-span-6 flex flex-col justify-center">
                            <motion.div 
                                initial={{ opacity: 0, x: -30 }} 
                                animate={{ opacity: 1, x: 0 }} 
                                transition={{ duration: 0.8 }}
                                className="space-y-6"
                            >
                                {tag && (
                                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 w-fit">
                                        <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                                        <span className="text-[10px] font-mono text-purple-300 uppercase tracking-widest font-semibold">{tag}</span>
                                    </div>
                                )}
                                
                                <h1 className="text-5xl md:text-6xl xl:text-7xl font-light tracking-tight text-white leading-[1.05]">
                                    {title === "The Context Layer for Agentic AI" ? (
                                        <>
                                            The Context Layer for <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-indigo-300 to-indigo-400">Agentic AI</span>
                                        </>
                                    ) : (
                                        title
                                    )}
                                </h1>
                                
                                <p className="text-base md:text-lg text-zinc-400 leading-relaxed max-w-xl font-light">
                                    {description}
                                </p>
                            </motion.div>

                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4, duration: 0.8 }}
                                className="mt-8 flex flex-wrap gap-6 text-xs font-mono text-zinc-500 border-t border-zinc-900 pt-6"
                            >
                                {serviceScope && (
                                    <div className="flex flex-col gap-1 pr-6 border-r border-zinc-900">
                                        <span className="text-zinc-600 text-[10px] uppercase">Service Scope</span>
                                        <span className="text-zinc-300">{serviceScope}</span>
                                    </div>
                                )}
                                {clientFocus && (
                                    <div className="flex flex-col gap-1 pr-6 border-r border-zinc-900">
                                        <span className="text-zinc-600 text-[10px] uppercase">Client Focus</span>
                                        <span className="text-zinc-300">{clientFocus}</span>
                                    </div>
                                )}
                                {buildWindow && (
                                    <div className="flex flex-col gap-1">
                                        <span className="text-zinc-600 text-[10px] uppercase">Build Window</span>
                                        <span className="text-zinc-300">{buildWindow}</span>
                                    </div>
                                )}
                            </motion.div>
                        </div>

                        {/* Right Column: Interactive Chat Sandbox */}
                        <div className="lg:col-span-6 w-full flex justify-center">
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.96 }} 
                                animate={{ opacity: 1, scale: 1 }} 
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="w-full max-w-lg bg-[#0F0F12] border border-zinc-800 rounded-2xl overflow-hidden shadow-[0_25px_60px_rgba(0,0,0,0.6)] flex flex-col relative group"
                            >
                                {/* Window Chrome */}
                                <div className="flex items-center justify-between px-4 py-3.5 bg-[#141418] border-b border-zinc-800/80">
                                    <div className="flex gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
                                        <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                                        <div className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
                                    </div>
                                    <div className="text-[10px] font-mono text-zinc-400 flex items-center gap-1.5 bg-zinc-900/60 px-3 py-1 rounded border border-zinc-800/30">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
                                        memorylayer_agent_sandbox_v1.0
                                    </div>
                                    <div className="text-[10px] font-mono text-purple-400 uppercase tracking-widest font-semibold">Memory Layer: ON</div>
                                </div>

                                {/* Live Metrics Panel */}
                                <div className="grid grid-cols-3 border-b border-zinc-800/80 bg-[#121216] px-4 py-2 text-[9px] font-mono text-zinc-500">
                                    <div>PGVECTOR RETRIEVAL: <span className="text-emerald-400">18ms</span></div>
                                    <div className="text-center">VRAM BUFFER: <span className="text-purple-400">92%</span></div>
                                    <div className="text-right">SSE STATE: <span className="text-[#10B981]">ACTIVE</span></div>
                                </div>

                                {/* Chat Mockup Display */}
                                <div className="p-4 flex-1 h-[270px] overflow-y-auto space-y-4 font-mono text-xs">
                                    <div className="text-zinc-600 text-[10px] text-center py-1 border-b border-zinc-900/40">
                                        [SYSTEM: Active Preset Vector Blueprints Initialized]
                                    </div>
                                    
                                    <div className="space-y-1">
                                        <span className="text-[10px] text-purple-400 font-bold">MemoryLayer Companion V1</span>
                                        <div className="bg-[#16161B] p-3 rounded-xl rounded-tl-none border border-zinc-800/60 text-[#ECECF1] leading-relaxed max-w-[85%]">
                                            Hello! I have scanned our persistent memory registers. The prompt directive asks me to explain the low-latency vector infrastructure we designed.
                                        </div>
                                    </div>

                                    <div className="space-y-1 flex flex-col items-end">
                                        <span className="text-[10px] text-zinc-500 font-bold">You (Sandbox Tester)</span>
                                        <div className="bg-purple-950/30 border border-purple-500/20 p-3 rounded-xl rounded-tr-none text-purple-200 leading-relaxed max-w-[85%]">
                                            What is the target threshold for our first-token latency budgets?
                                        </div>
                                    </div>

                                    <div className="space-y-1">
                                        <span className="text-[10px] text-purple-400 font-bold">MemoryLayer Companion V1</span>
                                        <div className="bg-[#16161B] p-3 rounded-xl rounded-tl-none border border-zinc-800/60 text-[#ECECF1] leading-relaxed max-w-[85%] relative overflow-hidden">
                                            Our performance target is sub-100ms. We achieve this by streaming tokens using Server-Sent Events directly from our Python/FastAPI execution layer.
                                            <span className="inline-block w-1.5 h-3.5 bg-purple-400 ml-1 animate-pulse" />
                                        </div>
                                    </div>
                                </div>

                                {/* Mini Options Footer */}
                                <div className="px-4 py-2.5 bg-[#141418] border-t border-zinc-800/80 flex items-center justify-between text-[10px] font-mono text-zinc-500">
                                    <span>Dialect: English (Custom accent)</span>
                                    <span>Temp: 0.72 | Top P: 0.90</span>
                                </div>
                            </motion.div>
                        </div>

                    </div>
                </div>
            </section>

            {/* Performance Ribbon */}
            {stats && stats.length > 0 && (
                <section className="border-t border-b border-zinc-800 bg-[#070709] py-10 z-20 relative w-full">
                    <div className="container mx-auto px-6 md:px-12 lg:px-24">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 divide-y md:divide-y-0 md:divide-x divide-zinc-800">
                            {stats.map((stat, i) => (
                                <div key={i} className="flex flex-col items-center md:items-start md:px-8 first:pl-0 pt-4 md:pt-0">
                                    <span className="text-3xl md:text-4xl font-light text-white font-mono tracking-tight">{stat.metric}</span>
                                    <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest mt-2">{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
